import React from "react";
import { CalendarOutlined, MessageOutlined } from "@ant-design/icons";
import {
  formatBrandKpiHeroDateRange,
  resolveBrandKpiHeroValue,
  resolveBrandKpiSentimentBreakdown,
  resolveBrandKpiIntentBreakdown,
} from "./welcomeBrandKpiHero";
import {
  WelcomeWidgetStatusTiming,
} from "pages/Welcome/welcomeWidgetJobTiming";
import { DASHBOARD_INTENT_COLORS } from "constants/dashboardIntentColors";
import { getIntlLocale } from "util/locale";
import styles from "pages/Welcome/style.module.scss";

const INTENT_HERO_COLORS = {
  appraisal: DASHBOARD_INTENT_COLORS["1"],
  complaint: DASHBOARD_INTENT_COLORS["2"],
  information: DASHBOARD_INTENT_COLORS["6"],
  request: DASHBOARD_INTENT_COLORS["7"],
  mixed: DASHBOARD_INTENT_COLORS["33"],
};

function BrandKpiMiniStat({ label, value, countLabel, toneClass, dotColor }) {
  return (
    <div className={styles.brandKpiHeroMiniStat}>
      <span className={styles.brandKpiHeroMiniStatLabel}>
        {dotColor ? (
          <span
            className={styles.brandKpiHeroIntentDot}
            style={{ background: dotColor }}
            aria-hidden
          />
        ) : null}
        {label}
      </span>
      <span
        className={`${styles.brandKpiHeroMiniStatValue} ${toneClass}`.trim()}
        style={dotColor ? { color: dotColor } : undefined}
      >
        {value}
      </span>
      {countLabel ? (
        <span className={styles.brandKpiHeroMiniStatSub}>{countLabel}</span>
      ) : null}
    </div>
  );
}

function BrandKpiMixedInnerBreakdown({ breakdown, t, nfCount }) {
  const mixed = breakdown?.mixed;
  const inner = breakdown?.mixedInner;
  if (!mixed || !inner?.percentages) {
    return null;
  }
  const { positive, neutral, negative } = inner.percentages;
  const totalInner = positive + neutral + negative;
  if (totalInner <= 0) {
    return null;
  }
  const legend = [
    {
      key: "positive",
      pct: positive,
      label: t("welcome.sentimentPositive"),
      className: styles.brandKpiHeroMixedSegPositive,
      dotClassName: styles.brandKpiHeroMixedDotPositive,
    },
    {
      key: "neutral",
      pct: neutral,
      label: t("welcome.sentimentNeutral"),
      className: styles.brandKpiHeroMixedSegNeutral,
      dotClassName: styles.brandKpiHeroMixedDotNeutral,
    },
    {
      key: "negative",
      pct: negative,
      label: t("welcome.sentimentNegative"),
      className: styles.brandKpiHeroMixedSegNegative,
      dotClassName: styles.brandKpiHeroMixedDotNegative,
    },
  ].filter((row) => row.pct > 0);

  return (
    <div className={styles.brandKpiHeroMixedBox}>
      <div className={styles.brandKpiHeroMixedBoxTitle}>
        {t("welcome.brandKpiMixedInnerTitle")}
      </div>
      <div className={styles.brandKpiHeroMixedBoxDesc}>
        {t("welcome.brandKpiMixedInnerDesc")}
      </div>
      <div className={styles.brandKpiHeroMixedBar} aria-hidden>
        {legend.map((row) => (
          <span
            key={row.key}
            className={row.className}
            style={{ width: `${row.pct}%` }}
          />
        ))}
      </div>
      <div className={styles.brandKpiHeroMixedLegend}>
        {legend.map((row) => (
          <span key={row.key} className={styles.brandKpiHeroMixedLegendItem}>
            <span className={row.dotClassName} aria-hidden />
            {row.label} {row.pct}%
          </span>
        ))}
      </div>
      {inner.counts && mixed.count != null ? (
        <div className={styles.brandKpiHeroMixedBoxMeta}>
          n={nfCount.format(mixed.count)}
        </div>
      ) : null}
    </div>
  );
}

function BrandKpiIntentMixedInnerBreakdown({ breakdown, t, nfCount }) {
  const mixed = breakdown?.mixed;
  const inner = breakdown?.mixedInner;
  if (!mixed || !inner?.percentages) {
    return null;
  }
  const legend = [
    {
      key: "appraisal",
      pct: inner.percentages.appraisal,
      label: t("welcome.intentAppraisal"),
      color: INTENT_HERO_COLORS.appraisal,
    },
    {
      key: "complaint",
      pct: inner.percentages.complaint,
      label: t("welcome.intentComplaint"),
      color: INTENT_HERO_COLORS.complaint,
    },
    {
      key: "information",
      pct: inner.percentages.information,
      label: t("welcome.intentInformation"),
      color: INTENT_HERO_COLORS.information,
    },
    {
      key: "request",
      pct: inner.percentages.request,
      label: t("welcome.intentRequest"),
      color: INTENT_HERO_COLORS.request,
    },
  ].filter((row) => Number(row.pct) > 0);

  if (!legend.length) {
    return null;
  }

  return (
    <div className={styles.brandKpiHeroMixedBox}>
      <div className={styles.brandKpiHeroMixedBoxTitle}>
        {t("welcome.brandKpiIntentMixedInnerTitle")}
      </div>
      <div className={styles.brandKpiHeroMixedBoxDesc}>
        {t("welcome.brandKpiIntentMixedInnerDesc")}
      </div>
      <div className={styles.brandKpiHeroMixedBar} aria-hidden>
        {legend.map((row) => (
          <span
            key={row.key}
            style={{ width: `${row.pct}%`, background: row.color, display: "block", height: "100%" }}
          />
        ))}
      </div>
      <div className={styles.brandKpiHeroMixedLegend}>
        {legend.map((row) => (
          <span key={row.key} className={styles.brandKpiHeroMixedLegendItem}>
            <span
              className={styles.brandKpiHeroIntentDot}
              style={{ background: row.color }}
              aria-hidden
            />
            {row.label} {row.pct}%
          </span>
        ))}
      </div>
      {mixed.count != null ? (
        <div className={styles.brandKpiHeroMixedBoxMeta}>
          n={nfCount.format(mixed.count)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Hero layout for brand KPI widgets (metrics 9, 10 & 29).
 */
export default function WelcomeBrandKpiHeroCard({
  metricId,
  header,
  widget,
  since,
  until,
  t,
  i18n,
  isPageDataLoading = false,
  isWidgetVisible = true,
  subtitleOverride,
}) {
  const mid = Number(metricId);
  const value = resolveBrandKpiHeroValue(header, mid);
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  const locale = i18n?.language;
  const nf = new Intl.NumberFormat(
    getIntlLocale(locale),
    mid === 9 ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : {},
  );
  const nfCount = new Intl.NumberFormat(
    getIntlLocale(locale),
  );

  const titleKey = `welcome.metricName_${mid}`;
  const titleTr = t(titleKey);
  const title =
    titleTr !== titleKey ? titleTr : String(header?.title ?? "").trim();

  const pivotSubtitle = (() => {
    if (subtitleOverride != null) {
      const override = String(subtitleOverride).trim();
      if (override) {
        return override;
      }
    }
    const raw = widget?.pivot_values;
    const vals = Array.isArray(raw)
      ? raw
          .filter((v) => v != null && String(v).trim() !== "")
          .map((v) => String(v).trim())
      : raw != null && String(raw).trim() !== ""
        ? [String(raw).trim()]
        : [];
    if (vals.length) return vals.join(", ");
    return String(header?.subtitle ?? "").trim() || null;
  })();

  const periodStart = header?.period_start ?? since ?? widget?.since;
  const periodEnd = header?.period_end ?? until ?? widget?.until;
  const periodLabel = formatBrandKpiHeroDateRange(
    periodStart,
    periodEnd,
    locale,
  );

  const reviewCountRaw = header?.review_count ?? header?.num_of_reviews;
  const reviewCount =
    reviewCountRaw != null &&
    reviewCountRaw !== "" &&
    Number.isFinite(Number(reviewCountRaw))
      ? Number(reviewCountRaw)
      : null;
  const reviewCountLabel =
    reviewCount != null ? nfCount.format(reviewCount) : null;

  const scale = Number(header?.scale) > 0 ? Number(header.scale) : 5;
  const trendDelta = header?.trend_delta ?? header?.trend;
  const trendNum =
    trendDelta != null && trendDelta !== "" && Number.isFinite(Number(trendDelta))
      ? Number(trendDelta)
      : null;

  const sentimentBreakdown =
    mid === 10 ? resolveBrandKpiSentimentBreakdown(header) : null;
  const intentBreakdown =
    mid === 29 ? resolveBrandKpiIntentBreakdown(header) : null;
  const isPercentHero = mid === 10 || mid === 29;
  const progressColor =
    mid === 29 ? INTENT_HERO_COLORS.appraisal : undefined;

  const formatBucketCount = (count) =>
    count != null
      ? t("welcome.brandKpiReviewCount", {
          count: nfCount.format(count),
        })
      : null;

  return (
    <div
      className={styles.brandKpiHeroCard}
      style={
        mid === 29
          ? { borderLeftColor: INTENT_HERO_COLORS.appraisal }
          : undefined
      }
    >
      <span className={styles.brandKpiHeroCategory}>
        {t("welcome.brandKpiCategoryLabel")}
      </span>

      <h3 className={styles.brandKpiHeroTitle}>{title}</h3>
      {pivotSubtitle ? (
        <p className={styles.brandKpiHeroSubtitle}>{pivotSubtitle}</p>
      ) : null}

      <div className={styles.brandKpiHeroValueRow}>
        {mid === 9 ? (
          <>
            <span className={styles.brandKpiHeroValueMain}>
              {nf.format(value)}
            </span>
            <span className={styles.brandKpiHeroValueScale}>
              / {scale}
            </span>
          </>
        ) : (
          <>
            <span
              className={styles.brandKpiHeroValueMain}
              style={mid === 29 ? { color: INTENT_HERO_COLORS.appraisal } : undefined}
            >
              {nf.format(value)}%
            </span>
            {trendNum != null && trendNum !== 0 ? (
              <span
                className={
                  trendNum > 0
                    ? styles.brandKpiHeroTrendUp
                    : styles.brandKpiHeroTrendDown
                }
              >
                {trendNum > 0 ? "↑" : "↓"}
                {t("welcome.brandKpiTrendPoints", {
                  count: Math.abs(trendNum),
                })}
              </span>
            ) : null}
          </>
        )}
      </div>

      {isPercentHero ? (
        <>
          <div className={styles.brandKpiHeroProgressTrack}>
            <div
              className={styles.brandKpiHeroProgressFill}
              style={{
                width: `${Math.min(100, Math.max(0, value))}%`,
                ...(progressColor ? { background: progressColor } : {}),
              }}
            />
            <span className={styles.brandKpiHeroProgressLabel}>
              {nf.format(value)}%
            </span>
          </div>
          {mid === 10 && sentimentBreakdown?.positive?.count != null ? (
            <span className={styles.brandKpiHeroPositiveCountSub}>
              {formatBucketCount(sentimentBreakdown.positive.count)}
            </span>
          ) : null}
          {mid === 29 && intentBreakdown?.appraisal?.count != null ? (
            <span
              className={styles.brandKpiHeroPositiveCountSub}
              style={{ color: `${INTENT_HERO_COLORS.appraisal}d9` }}
            >
              {formatBucketCount(intentBreakdown.appraisal.count)}
            </span>
          ) : null}
        </>
      ) : null}

      {sentimentBreakdown ? (
        <>
          <div className={styles.brandKpiHeroSecondaryStats}>
            {sentimentBreakdown.neutral ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiNeutralRate")}
                value={`${sentimentBreakdown.neutral.percentage}%`}
                countLabel={formatBucketCount(sentimentBreakdown.neutral.count)}
                toneClass={styles.brandKpiHeroMiniStatNeutral}
              />
            ) : null}
            {sentimentBreakdown.mixed ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiMixedRate")}
                value={`${sentimentBreakdown.mixed.percentage}%`}
                countLabel={formatBucketCount(sentimentBreakdown.mixed.count)}
                toneClass={styles.brandKpiHeroMiniStatMixed}
              />
            ) : null}
            {sentimentBreakdown.negative ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiNegativeRate")}
                value={`${sentimentBreakdown.negative.percentage}%`}
                countLabel={formatBucketCount(sentimentBreakdown.negative.count)}
                toneClass={styles.brandKpiHeroMiniStatNegative}
              />
            ) : null}
          </div>
          <BrandKpiMixedInnerBreakdown
            breakdown={sentimentBreakdown}
            t={t}
            nfCount={nfCount}
          />
        </>
      ) : null}

      {intentBreakdown ? (
        <>
          <div
            className={`${styles.brandKpiHeroSecondaryStats} ${styles.brandKpiHeroSecondaryStatsFour}`.trim()}
          >
            {intentBreakdown.complaint ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiComplaintRate")}
                value={`${intentBreakdown.complaint.percentage}%`}
                countLabel={formatBucketCount(intentBreakdown.complaint.count)}
                dotColor={INTENT_HERO_COLORS.complaint}
              />
            ) : null}
            {intentBreakdown.information ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiInformationRate")}
                value={`${intentBreakdown.information.percentage}%`}
                countLabel={formatBucketCount(intentBreakdown.information.count)}
                dotColor={INTENT_HERO_COLORS.information}
              />
            ) : null}
            {intentBreakdown.request ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiRequestRate")}
                value={`${intentBreakdown.request.percentage}%`}
                countLabel={formatBucketCount(intentBreakdown.request.count)}
                dotColor={INTENT_HERO_COLORS.request}
              />
            ) : null}
            {intentBreakdown.mixed ? (
              <BrandKpiMiniStat
                label={t("welcome.brandKpiMixedIntentRate")}
                value={`${intentBreakdown.mixed.percentage}%`}
                countLabel={formatBucketCount(intentBreakdown.mixed.count)}
                dotColor={INTENT_HERO_COLORS.mixed}
              />
            ) : null}
          </div>
          <BrandKpiIntentMixedInnerBreakdown
            breakdown={intentBreakdown}
            t={t}
            nfCount={nfCount}
          />
        </>
      ) : null}

      {(periodLabel || reviewCountLabel != null) && (
        <div className={styles.brandKpiHeroMetaRow}>
          {periodLabel ? (
            <span className={styles.brandKpiHeroMetaItem}>
              <CalendarOutlined aria-hidden />
              {periodLabel}
            </span>
          ) : null}
          {periodLabel && reviewCountLabel != null ? (
            <span className={styles.brandKpiHeroMetaSep} aria-hidden>
              |
            </span>
          ) : null}
          {reviewCountLabel != null ? (
            <span className={styles.brandKpiHeroMetaItem}>
              <MessageOutlined aria-hidden />
              {t("welcome.brandKpiReviewCount", { count: reviewCountLabel })}
            </span>
          ) : null}
        </div>
      )}

      <WelcomeWidgetStatusTiming
        widget={widget}
        t={t}
        className={styles.brandKpiHeroTimingFooter}
        isPageDataLoading={isPageDataLoading}
        isWidgetVisible={isWidgetVisible}
        hideCreationTiming
        hidePageLoadTiming
        pageLoadVariant="footer"
      />
    </div>
  );
}
